import React, { useEffect, useMemo, useState } from 'react';
import { fetchBatchGroups } from '../../../api/adminApi';

const TRACK_LABELS = {
  foundation: 'Foundation',
  jee: 'JEE',
  smartplus: 'Smart Plus',
  other: 'Other',
};

/**
 * Rendering iterates this list, so a track missing from it is silently dropped
 * from the screen. It must stay in step with the tracks `util/batchGrouping.js`
 * emits.
 */
const TRACK_ORDER = ['foundation', 'jee', 'smartplus', 'other'];

/**
 * The Month Planner's first screen: choose what to plan for, and nothing else.
 *
 * Rows come from the server already split by live group, so picking a row picks
 * both the batch group and the live group in one click — a live class runs for
 * one live group at a time, and asking for them as two separate questions was
 * the confusing part of the old single-screen flow.
 *
 * Selecting a row hands it to the parent, which swaps in the planning screen.
 */
const GroupList = ({ onSelect }) => {
  const [groups, setGroups] = useState([]);
  const [totals, setTotals] = useState({ batches: 0, groups: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [trackFilter, setTrackFilter] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const data = await fetchBatchGroups(false, true);
        if (!active) return;
        setGroups(data.groups || []);
        setTotals({ batches: data.totalBatches || 0, groups: data.totalGroups || 0 });
      } catch (loadError) {
        if (active) setError('Failed to load batch groups.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const visibleGroups = useMemo(() => {
    const term = search.trim().toLowerCase();
    return groups.filter((group) => {
      if (trackFilter && group.track !== trackFilter) return false;
      if (!term) return true;
      if ((group.label || '').toLowerCase().includes(term)) return true;
      return (group.batches || []).some(
        (batch) => (batch.name || '').toLowerCase().includes(term)
      );
    });
  }, [groups, trackFilter, search]);

  if (loading) {
    return <div className="sb-panel"><span className="cal-empty">Loading batch groups…</span></div>;
  }
  if (error) {
    return <div className="sb-panel"><div className="cal-error">{error}</div></div>;
  }

  return (
    <div className="sb-panel">
      <div className="sb-panel-head">
        <h4>Choose a group to plan</h4>
        <span className="sb-selcount">{totals.groups} groups · {totals.batches} batches</span>
      </div>

      <div className="sb-pickrow" style={{ marginBottom: 10 }}>
        <input
          type="search"
          value={search}
          placeholder="Search group or batch…"
          onChange={(event) => setSearch(event.target.value)}
          aria-label="Search batch groups"
        />
        <select
          className="cal-select"
          value={trackFilter}
          onChange={(event) => setTrackFilter(event.target.value)}
          aria-label="Track"
        >
          <option value="">All tracks</option>
          {TRACK_ORDER.map((track) => (
            <option key={track} value={track}>{TRACK_LABELS[track]}</option>
          ))}
        </select>
      </div>

      {TRACK_ORDER.map((track) => {
        const trackGroups = visibleGroups.filter((group) => group.track === track);
        if (trackGroups.length === 0) return null;

        return (
          <div key={track} className={`sb-track sb-track-${track}`}>
            <div className="sb-track-head">
              <span className="sb-track-dot" aria-hidden="true" />
              <span className="sb-track-name">{TRACK_LABELS[track]}</span>
              <span className="sb-track-count">
                {trackGroups.length} groups ·{' '}
                {trackGroups.reduce((total, group) => total + group.batchCount, 0)} batches
              </span>
            </div>

            {trackGroups.map((group) => (
              <button
                key={group.key}
                type="button"
                className="sb-grouprow-button"
                onClick={() => onSelect(group)}
                disabled={group.batchCount === 0}
              >
                <span className="sb-group-label">
                  <span className="sb-group-name">{group.label}</span>
                  <span className="sb-group-meta">
                    {(group.batches || []).slice(0, 3).map((batch) => batch.name).join(' · ')}
                    {group.batchCount > 3 ? ` · +${group.batchCount - 3} more` : ''}
                  </span>
                </span>
                <span className="sb-badge">
                  {group.batchCount === 0 ? 'No batches' : `${group.batchCount} batches`}
                </span>
                <span className="sb-grouprow-chevron" aria-hidden="true">›</span>
              </button>
            ))}
          </div>
        );
      })}

      {visibleGroups.length === 0 && <span className="cal-empty">No groups match.</span>}
    </div>
  );
};

export default GroupList;
